import {map} from 'lodash';
import moment from 'moment-timezone';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Select} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {dateUtils} from 'utils';

const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Props = {
    hasError?: boolean;
    errorMessage?: string;
    readonly?: boolean;
    timeIncrement?: number;
    hasEmptyOption?: boolean;
    placeholder?: string;
    timeZone: string;
    timeFormat: string;
    value: {
        hours: number;
        minutes: number;
    },
    className?: string;
    onChange: (value: {hours: number, minutes: number}) => void;
}

export type TimePickerProps = Props;

const TIME_INCREMENT = 15;

export const TimePicker: React.FC<Props> = ({
    value, hasError, errorMessage, readonly, placeholder, timeIncrement = TIME_INCREMENT, hasEmptyOption,
    timeZone, timeFormat, className, onChange
}) => {
    const {t} = useTranslation();

    const nowMoment = useMemo(() => moment.tz(timeZone), [timeZone]);
    const inputValue = useMemo(() => {
        if (!value) {
            return '';
        }
        const now = moment().tz(timeZone);
        now.set('h', value.hours).set('m', value.minutes);
        return now.format(timeFormat);
    }, [value, timeZone, timeFormat]);
    const [hasFormatError, setFormatError] = useState(false);
    const isInvalid = useMemo(() => hasError || hasFormatError, [hasError, hasFormatError]);

    const timeItems: string[] = useMemo(() => dateUtils.getRange(
        nowMoment.clone().startOf('day'), nowMoment.clone().endOf('day'),
        'minutes', timeFormat, {includeEndDate: true, increment: timeIncrement}), [timeIncrement, timeFormat, nowMoment]);

    const timeOptions = useMemo(() => map(timeItems, (item) => ({id: item, name: item})), [timeItems]);

    const parseInputValue = useCallback((val: string) => {
        try {
            const [hoursString, minutes] = val.split(/[: ]/);
            let hours = +hoursString;
            if (hours !== 12 && val.includes('pm')) {
                hours += 12;
            } else if (hours === 12 && val.includes('am')) {
                hours = 0;
            }
            return {valid: true, value: {hours, minutes: +minutes}};
        } catch (error) {
            return {valid: false};
        }
    }, []);

    const handleChange = useCallback((time: string) => {
        const {valid, value: val} = parseInputValue(time);
        setFormatError(false);
        if (valid) {
            onChange(val);
        } else if (!time && hasEmptyOption) {
            onChange(null);
        } else {
            setFormatError(true);
        }
    }, [parseInputValue, hasEmptyOption, onChange]);

    return (
        <Select
            noneOption={hasEmptyOption}
            noneOptionLabel={t(T_KEYS_COMMON.ALL.NONE)}
            placeholder={placeholder}
            value={inputValue}
            options={timeOptions}
            valueAs='id'
            disabled={readonly}
            hasError={isInvalid}
            errorMessage={errorMessage}
            isSearchable
            className={className}
            onChange={handleChange}
        />
    );
};